import {useMemo} from "react";
import {useIdentityAuth} from "../context/IdentityAuthContext";
import {decodeToken} from "../utils/identityTokens";

export const BACKOFFICE_ROLES = {
  ADMIN: "IDENTITY.Admin",
  OWNER: "IDENTITY.Owner",
  VIEWER: "IDENTITY.Viewer"
};

export const ACTIONS = {
  READ: "read",
  WRITE: "write",
  WRITE_ROLES: "write-roles",
  DELETE: "delete",
  DELETE_ROLES: "delete-roles"
};

export const RESOURCES = {
  USERS: "users",
  USER_ROLES: "users.roles",

  TENANTS: "tenants",
  TENANT_PRODUCTS: "tenants.products",
  TENANT_USERS: "tenants.users",
  TENANT_ROLES: "tenants.roles",

  PRODUCTS: "products",
  ROLES: "roles",
  SCOPES: "scopes",

  CLIENTS: "clients",
  TOKENS: "tokens",
  M2M_TOKENS: "m2m_tokens",
  TICKETS: "tickets"
};

export const useAuthPermissions = () => {
  const {isAuthenticated, accessToken} = useIdentityAuth();

  // Decodificar el token una sola vez y reutilizar
  const decodedPayload = useMemo(() => {
    if (!isAuthenticated || !accessToken) return null;
    return decodeToken(accessToken);
  }, [isAuthenticated, accessToken]);

  /**
   * Verifica si el usuario tiene al menos uno de los roles indicados.
   * @param {string | string[]} roleToVerify
   * @returns {boolean}
   */
  const verifyRole = (roleToVerify) => {
    if (!decodedPayload) return false;

    const roles = decodedPayload?.roles;
    if (!roles || !Array.isArray(roles)) return false;

    const rolesToVerify = Array.isArray(roleToVerify) ? roleToVerify : [roleToVerify];
    return rolesToVerify.some((role) =>
      roles.some((userRole) => typeof userRole === "string" && userRole.toLowerCase() === role.toLowerCase())
    );
  };

  /**
   * Verifica si el usuario tiene al menos uno de los scopes indicados.
   * @param {string | string[]} scopeToVerify
   * @returns {boolean}
   */
  const verifyScope = (scopeToVerify) => {
    if (!decodedPayload) return false;

    const scopes = decodedPayload?.scope;
    if (!scopes || !Array.isArray(scopes)) return false;

    const scopesToVerify = Array.isArray(scopeToVerify) ? scopeToVerify : [scopeToVerify];
    return scopesToVerify.some((scope) =>
      scopes.some((userScope) => typeof userScope === "string" && userScope.toLowerCase() === scope.toLowerCase())
    );
  };

  /**
   * Comprueba si el usuario tiene permiso para realizar una acción sobre un recurso.
   * Genera el scope: identity.{resource}.global.{action}
   * @param {string} action  - ACTIONS.READ | ACTIONS.WRITE | ACTIONS.DELETE
   * @param {string} resource - RESOURCES.*
   * @returns {boolean}
   */
  const can = (action, resource) => verifyScope(`identity.${resource}.global.${action}`);

  return {verifyRole, verifyScope, can};
};
